/* =====================================================================
   MOMENTUM — premium motion engine. Vanilla JS, no dependencies.
   One rAF loop, batched DOM writes, IntersectionObserver for reveals.
   All effects degrade gracefully and respect prefers-reduced-motion.
   ===================================================================== */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  // Signal CSS that JS is live (content was visible until now).
  docEl.classList.add('mo-ready');
  if (reduce) return; // everything below is motion — bail entirely.

  /* =============== 1. REVEAL + STAGGER =============== */
  var revealSweep = function () {};
  function setupReveals() {
    // Auto-tag common content blocks. Containers stagger their children.
    var staggerGroups = [
      ['.section-head', '> *', 90],
      ['.verticals-grid', '> *', 110],
      ['.product-grid', '> *', 70],
      ['.countdown', '> *', 80],
      ['.footer-grid', '> *', 90],
      ['.gallery-grid', '> *', 60]
    ];
    staggerGroups.forEach(function (g) {
      $$(g[0]).forEach(function (group) {
        var kids = $$(g[1].replace('> ', ':scope > '), group);
        if (!kids.length) kids = Array.prototype.slice.call(group.children);
        kids.forEach(function (el, i) {
          if (el.classList.contains('mo-skip')) return;
          // headings are handled by the word-by-word splitter — don't double-animate
          if (el.tagName === 'H1' || el.tagName === 'H2') return;
          el.classList.add('mo-reveal');
          el.style.setProperty('--mo-delay', (i * g[2]) + 'ms');
        });
      });
    });

    // Standalone blocks (varied speeds for a layered feel).
    var singles = [
      ['.drop', 28, .9], ['.film__inner', 34, 1], ['.manifesto .big', 30, 1.05],
      ['.manifesto p', 24, .9], ['.news-band .wrap > *', 22, .8],
      ['.game-shell', 36, 1], ['.leaderboard', 24, .8],
      ['.auth-card', 30, .9], ['.policy-doc.is-active', 24, .8],
      ['.collection-head > *', 22, .8], ['.product-info > *', 20, .8],
      ['.media-gallery', 30, .9], ['.contact-info > *', 22, .8]
    ];
    singles.forEach(function (s) {
      $$(s[0]).forEach(function (el, i) {
        if (el.classList.contains('mo-reveal')) return;
        el.classList.add('mo-reveal');
        el.style.setProperty('--mo-y', s[1] + 'px');
        el.style.setProperty('--mo-dur', s[2] + 's');
      });
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('mo-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    $$('.mo-reveal').forEach(function (el) { io.observe(el); });

    // Safety net: never leave on-screen content hidden. If IO is throttled
    // or an element is already in view, force-reveal anything within the
    // viewport shortly after load.
    function sweep() {
      $$('.mo-reveal:not(.mo-in)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < (window.innerHeight || 0) + 40 && r.bottom > -40) {
          el.classList.add('mo-in'); io.unobserve(el);
        }
      });
    }
    revealSweep = sweep;
    window.addEventListener('load', function () { setTimeout(sweep, 200); });
    window.addEventListener('scroll', function () { sweep(); }, { passive: true });
    setTimeout(sweep, 1200);
  }

  /* =============== 2. WORD-BY-WORD HEADINGS =============== */
  function splitWords(el) {
    // Only split direct text nodes; preserve inline children (e.g. <em>, <span>).
    var nodes = Array.prototype.slice.call(el.childNodes);
    var wordIndex = 0;
    nodes.forEach(function (node) {
      if (node.nodeType === 3) {
        var frag = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(function (part) {
          if (part.trim() === '') { frag.appendChild(document.createTextNode(part)); return; }
          var span = document.createElement('span');
          span.className = 'mo-word';
          span.textContent = part;
          span.style.setProperty('--mo-wd', (wordIndex * 55) + 'ms');
          wordIndex++;
          frag.appendChild(span);
        });
        el.replaceChild(frag, node);
      } else if (node.nodeType === 1) {
        node.classList.add('mo-word');
        node.style.setProperty('--mo-wd', (wordIndex * 55) + 'ms');
        wordIndex++;
      }
    });
    el.classList.add('mo-split');
  }
  function setupHeadings() {
    var targets = [].concat(
      $$('.hero h1'),
      $$('.section-head h2'),
      $$('.manifesto .big'),
      $$('.film h2')
    );
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('mo-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.3 });
    targets.forEach(function (el) {
      if (el.dataset.moSplit) return;
      el.dataset.moSplit = '1';
      splitWords(el);
      io.observe(el);
    });
    function sweepHeads() {
      targets.forEach(function (el) {
        if (el.classList.contains('mo-in')) return;
        var r = el.getBoundingClientRect();
        if (r.top < (window.innerHeight || 0) + 40 && r.bottom > -40) { el.classList.add('mo-in'); io.unobserve(el); }
      });
    }
    window.addEventListener('load', function () { setTimeout(sweepHeads, 200); });
    setTimeout(sweepHeads, 1200);
  }

  /* =============== 3. PARALLAX (rAF, batched) =============== */
  var parallaxItems = [];
  function setupParallax() {
    // hero media slow; section images subtle.
    $$('.hero__media').forEach(function (el) { el.setAttribute('data-parallax', '0.18'); });
    $$('.film__media').forEach(function (el) { el.setAttribute('data-parallax', '0.12'); });
    $$('.vcard__img').forEach(function (el) { el.setAttribute('data-parallax', '0.08'); });
    parallaxItems = $$('[data-parallax]').map(function (el) {
      return { el: el, speed: parseFloat(el.getAttribute('data-parallax')) || 0.1, py: 0 };
    });
  }

  /* =============== 4. MAGNETIC BUTTONS =============== */
  function setupMagnetic() {
    if (!finePointer) return;
    $$('.btn').forEach(function (btn) {
      if (btn.classList.contains('btn--block')) return; // full-width buttons skip
      btn.classList.add('mo-magnetic');
      var rect;
      btn.addEventListener('mouseenter', function () { rect = btn.getBoundingClientRect(); });
      btn.addEventListener('mousemove', function (e) {
        if (!rect) rect = btn.getBoundingClientRect();
        var mx = e.clientX - rect.left - rect.width / 2;
        var my = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = 'translate(' + (mx * 0.28) + 'px,' + (my * 0.38) + 'px)';
      });
      btn.addEventListener('mouseleave', function () { btn.style.transform = ''; rect = null; });
    });
  }

  /* =============== 5. CUSTOM CURSOR =============== */
  var cursor = { x: 0, y: 0, tx: 0, ty: 0, dx: 0, dy: 0 };
  var cursorEl, dotEl, cursorOn = false;
  function setupCursor() {
    if (!finePointer) return;
    if (window.MOMENTUM && window.MOMENTUM.motionCursor === false) return;
    cursorOn = true;
    docEl.classList.add('mo-cursor-on');
    cursorEl = document.createElement('div'); cursorEl.className = 'mo-cursor';
    dotEl = document.createElement('div'); dotEl.className = 'mo-cursor-dot';
    document.body.appendChild(cursorEl); document.body.appendChild(dotEl);
    window.addEventListener('mousemove', function (e) { cursor.tx = e.clientX; cursor.ty = e.clientY; }, { passive: true });
    // grow over interactive targets
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest('a, button, input, textarea, select, [role="button"], .pcard, .vcard')) cursorEl.classList.add('is-hot');
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest('a, button, input, textarea, select, [role="button"], .pcard, .vcard')) cursorEl.classList.remove('is-hot');
    });
  }

  /* =============== 6. MOUSE-REACTIVE GLOW =============== */
  function setupGlow() {
    if (!finePointer) return;
    var glowSelectors = '.drop, .vcard, .pcard, .game-shell, .auth-card, .vault-box, .cd-cell, .filter-group';
    $$(glowSelectors).forEach(function (el) { el.classList.add('mo-glow'); });
    document.addEventListener('mousemove', function (e) {
      var el = e.target.closest('.mo-glow');
      if (!el) return;
      var r = el.getBoundingClientRect();
      el.style.setProperty('--gx', ((e.clientX - r.left) / r.width * 100) + '%');
      el.style.setProperty('--gy', ((e.clientY - r.top) / r.height * 100) + '%');
    }, { passive: true });
    document.addEventListener('mouseover', function (e) {
      var el = e.target.closest('.mo-glow'); if (el) el.classList.add('is-near');
    });
    document.addEventListener('mouseout', function (e) {
      var el = e.target.closest('.mo-glow');
      if (el && !el.contains(e.relatedTarget)) el.classList.remove('is-near');
    });
  }

  /* =============== 7. AURORA + PARTICLES (hero) =============== */
  var particles = null;
  function setupAmbient() {
    // slow gradient accent behind hero + manifesto
    $$('.hero, .manifesto, .drop').forEach(function (host) {
      if ($('.mo-aurora', host)) return;
      var a = document.createElement('div'); a.className = 'mo-aurora';
      host.insertBefore(a, host.firstChild);
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    });
    // particle field in hero (desktop, lightweight)
    if (!finePointer) return;
    var hero = $('.hero');
    if (!hero) return;
    var canvas = document.createElement('canvas');
    canvas.className = 'mo-particles';
    hero.insertBefore(canvas, hero.querySelector('.hero__inner') || null);
    var ctx = canvas.getContext('2d');
    var pts = [], W = 0, H = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    var accent = getComputedStyle(docEl).getPropertyValue('--color-accent').trim() || '#A855F7';
    function size() {
      var r = hero.getBoundingClientRect(); W = r.width; H = r.height;
      canvas.width = W * dpr; canvas.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var count = Math.min(36, Math.round(W / 34));
      pts = [];
      for (var i = 0; i < count; i++) pts.push({ x: Math.random() * W, y: Math.random() * H, r: Math.random() * 1.6 + 0.4, vy: -(Math.random() * 0.25 + 0.05), vx: (Math.random() - 0.5) * 0.12, a: Math.random() * 0.5 + 0.15 });
    }
    size();
    window.addEventListener('resize', size);
    particles = {
      _draw: function () {
        ctx.clearRect(0, 0, W, H);
        for (var i = 0; i < pts.length; i++) {
          var p = pts[i];
          p.y += p.vy; p.x += p.vx;
          if (p.y < -5) { p.y = H + 5; p.x = Math.random() * W; }
          if (p.x < -5) p.x = W + 5;
          if (p.x > W + 5) p.x = -5;
          ctx.globalAlpha = p.a;
          ctx.fillStyle = accent;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
    };
  }

  /* =============== 8. ADD-TO-CART PULSE =============== */
  function setupCartPulse() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-cart-add], form[action*="/cart/add"]');
      if (!form) return;
      var btn = form.querySelector('[type="submit"]');
      if (btn) { btn.classList.remove('mo-added'); void btn.offsetWidth; btn.classList.add('mo-added'); }
    }, true);
  }

  /* =============== MAIN rAF LOOP =============== */
  var lastScroll = -1, vh = window.innerHeight, ticking = true;
  window.addEventListener('resize', function () { vh = window.innerHeight; }, { passive: true });
  document.addEventListener('visibilitychange', function () { ticking = !document.hidden; if (ticking) loop(); });

  function loop() {
    if (!ticking) return;
    var sy = window.pageYOffset;

    // parallax — only when scroll changed
    if (sy !== lastScroll) {
      lastScroll = sy;
      revealSweep();
      for (var i = 0; i < parallaxItems.length; i++) {
        var it = parallaxItems[i];
        var rect = it.el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) continue;
        var center = rect.top + rect.height / 2;
        var fromMid = center - vh / 2;
        it.el.style.setProperty('--mo-py', (-fromMid * it.speed).toFixed(1) + 'px');
      }
    }

    // cursor lerp
    if (cursorOn) {
      cursor.x += (cursor.tx - cursor.x) * 0.18;
      cursor.y += (cursor.ty - cursor.y) * 0.18;
      cursor.dx += (cursor.tx - cursor.dx) * 0.5;
      cursor.dy += (cursor.ty - cursor.dy) * 0.5;
      cursorEl.style.setProperty('--cx', cursor.x + 'px');
      cursorEl.style.setProperty('--cy', cursor.y + 'px');
      dotEl.style.setProperty('--dx', cursor.dx + 'px');
      dotEl.style.setProperty('--dy', cursor.dy + 'px');
    }

    // particles
    if (particles && particles._draw) particles._draw();

    requestAnimationFrame(loop);
  }

  function init() {
    setupReveals();
    setupHeadings();
    setupParallax();
    setupMagnetic();
    setupCursor();
    setupGlow();
    setupAmbient();
    setupCartPulse();
    requestAnimationFrame(loop);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
